import useScrollWindowToTop from '@app/src/hooks/useScrollWindowToTop'
import ResourceCollectionScene from '@app/src/pages/ResourceCollection'
import DataHubScene from '@app/src/pages/ResourceCollection/Collections/DataHubScene'
import EvaluationsCollection from '@app/src/pages/ResourceCollection/Collections/EvaluationsCollection'
import AccessorRequestsScene from '@app/src/pages/ResourceCollection/Collections/ManageRequests/AccessorRequestsScene'
import AccessorResponseScene from '@app/src/pages/ResourceCollection/Collections/ManageRequests/Response/AccessorResponseScene'
import MappingNodeCollection from '@app/src/pages/ResourceCollection/Collections/MappingNodeCollection'
import ActivityOverview from '@app/src/pages/ResourceCollection/Collections/Provider/ProviderActivity/ActivityOverview'
import ProviderContactsOverview from '@app/src/pages/ResourceCollection/Collections/ProviderContactsOverview'
import SourcingProviderProgressCollection from '@app/src/pages/ResourceCollection/Collections/SourcingProviderProgressCollection'
import type { Provider } from '@app/src/types/organizations'
import type { RelatedContentFilter, Schema } from '@app/src/types/schemas'
import get from 'lodash/get'
import React, { useMemo } from 'react'
import AssessmentsCompanyPageScene from '../Assessments/AssessmentCompanyPage/AssessmentsCompanyPageScene'
import ProviderRisksPage from '../ResourceCollection/Collections/RiskScreening/ProviderRisksPage'
import type { FilterGroup } from '../ResourceCollection/Filters/useFilters'
import { Operators } from '../ResourceCollection/Filters/useFilters'
import SustainabilityProfileScene from '../SustainabilityProfile/SustainabilityProfileScene'
import TopicalRiskCompanyPageScene from '../TopicalRisk/CompanyPage/TopicalRiskCompanyPageScene'
import DetailsTab from './DetailsTab'

export enum AllTabs {
  Details = 'details',
  Profile = 'profile',
  Contact = 'contact',
  TopicalRisk = 'topical-risk',
  Request = 'request',
  Response = 'response',
  ResponseItem = 'responseitem',
  Evaluation = 'evaluation',
  RiskScreening = 'risk-screening',
  ProviderProgress = 'providerProgress',
  MappingNode = 'mapping-node',
  Activity = 'activity',
  BaselineAssessment = 'base-assessment',
  Assessments = 'assessments',
}

interface ContentProps {
  parentResourceType: string
  provider?: Provider
  activeTabParam: AllTabs
  schema?: Schema
}

const getRelatedContentFilters = (
  filtersList: RelatedContentFilter[] = [],
  provider?: Provider,
  activeTabParam?: AllTabs,
): FilterGroup[] => {
  const filters: FilterGroup[] = filtersList.reduce((acc, filter) => {
    const value = get(provider, filter.accessor)
    if (value) {
      acc.push({
        name: filter.name,
        filters: [{ value, operator: filter.operator }],
      })
    }
    return acc
  }, [] as FilterGroup[])

  if (provider?.id) {
    const targetFilterMap: Record<string, string> = {
      [AllTabs.ResponseItem]: 'response.request.subscriptions.targetProviderId',
      [AllTabs.Response]: 'request.subscriptions.targetProviderId',
      [AllTabs.MappingNode]: 'providerId',
    }

    const filterName = targetFilterMap[activeTabParam || '']
    if (filterName) {
      filters.push({
        name: filterName,
        filters: [{ value: provider.id, operator: Operators.EqualTo }],
      })
    }
  }

  return filters
}

const Content: React.FC<ContentProps> = ({ parentResourceType, schema, provider, activeTabParam }) => {
  const relatedContentFilter = useMemo(() => {
    const filtersList = schema?.relatedContentFilters?.[parentResourceType] || []

    return getRelatedContentFilters(filtersList, provider, activeTabParam)
  }, [schema, parentResourceType, provider, activeTabParam])

  useScrollWindowToTop()

  if (!provider) {
    return null
  }

  switch (activeTabParam) {
    case AllTabs.Details:
      return <DetailsTab provider={provider} />
    case AllTabs.Profile:
      return <SustainabilityProfileScene provider={provider} />
    case AllTabs.Contact:
      return <ProviderContactsOverview />
    case AllTabs.TopicalRisk:
      return (
        <ResourceCollectionScene title="" embedded enableScroll>
          <TopicalRiskCompanyPageScene provider={provider} />
        </ResourceCollectionScene>
      )
    case AllTabs.Assessments:
      return <AssessmentsCompanyPageScene provider={provider} />
    case AllTabs.Request:
      return <AccessorRequestsScene embedded relatedContentFilter={relatedContentFilter} />
    case AllTabs.Response:
      return <AccessorResponseScene embedded relatedContentFilter={relatedContentFilter} />
    case AllTabs.ResponseItem:
      return <DataHubScene embedded relatedContentFilter={relatedContentFilter} />
    case AllTabs.Evaluation:
      return <EvaluationsCollection embedded relatedContentFilter={relatedContentFilter} />
    case AllTabs.RiskScreening:
      return <ProviderRisksPage providerId={provider.id} relatedContentFilter={relatedContentFilter} />
    case AllTabs.ProviderProgress:
      return (
        <SourcingProviderProgressCollection
          relatedContentFilter={relatedContentFilter}
          supplierId={provider.id}
          organizationId={provider.organizationId}
        />
      )
    case AllTabs.MappingNode:
      return <MappingNodeCollection embedded relatedContentFilter={relatedContentFilter} />
    case AllTabs.Activity:
      return <ActivityOverview provider={provider} />
    default:
      return null
  }
}

export default Content
